import { useTranslation } from 'react-i18next';
import React, { useEffect,useState } from 'react';
import { Button, Radio, Select, message, Divider, Modal, Tabs, Space } from 'antd';
import { useQuery, useMutation } from '@apollo/client';

import { APPEND_BIND_PRICING_SCHEME } from '../graphql/mutations/pricingSchemeApplyMutation';
import { GET_CLIENT_PRICING_SCHEME_APPLY_DETAIL } from '../graphql/queries/getClientPricingSchemeApplyDetail';
import { LIST_HUB_SORT_CODE_SELECT_OPTIONS } from '../graphql/queries/listAllHubSortCodeOptions';
import { LIST_WAREHOUSES_UNDER_HUB_OPTIONS } from '../graphql/queries/listWarehousesUnderHubOptions';

import { GET_PRICING_SCHEME_DETAIL, PricingSchemeDetailResponse } from 'graphql/queries/settlement/getPricingSchemeDetail';
import { ResourceCode } from 'constants/resourceCodes';
import { HasResource } from 'components/HasResource';
import { BUSINESS_SCENARIO_COST_PERIODIC, COST_PERIODIC_FEE_ITEM_CODE, COST_PERIODIC_BIND_ALL_VALUE } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { LIST_SCHEME_SELECT_OPTIONS } from '../../graphql/queries/listSchemeSelectOptions';
import { formatTierDiscountDisplay } from '../../periodic/CostPeriodicTierConfig';
import { resolvePricingSchemeMessage } from 'utils/graphqlUtils';
import { formatApplyTimeWithTimezone } from 'utils/timezoneFormatter';


interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CostPeriodicBindEditComp: React.FC<Props> = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [selectedSchemeId, setSelectedSchemeId] = useState<number | null>(null);
  // 追加绑定粒度：'HUB'=到 HUB（一段码，默认），'WAREHOUSE'=到 Warehouse（二段码）
  const [granularity, setGranularity] = useState<'HUB' | 'WAREHOUSE'>('HUB');
  const [newHubs, setNewHubs] = useState<string[]>([]);
  const [warehouseHub, setWarehouseHub] = useState<string | null>(null);
  const [newWarehouses, setNewWarehouses] = useState<string[]>([]);

  const { data: schemeOptions } = useQuery(LIST_SCHEME_SELECT_OPTIONS, {
    variables: { businessScenario: BUSINESS_SCENARIO_COST_PERIODIC, channelId: 0, isBind: true },
  });
  const { data: hubOptions } = useQuery(LIST_HUB_SORT_CODE_SELECT_OPTIONS);
  const { data: warehouseOptions } = useQuery(LIST_WAREHOUSES_UNDER_HUB_OPTIONS, {
    variables: { hubSortingCode: warehouseHub },
    skip: !warehouseHub,
  });
  const { data: applyDetail } = useQuery(GET_CLIENT_PRICING_SCHEME_APPLY_DETAIL, {
    variables: { clientId: 0, pricingSchemeId: selectedSchemeId },
    skip: !selectedSchemeId,
    fetchPolicy: 'network-only',
  });
  const { data: detailData } = useQuery<PricingSchemeDetailResponse>(GET_PRICING_SCHEME_DETAIL, {
    variables: { pricingSchemeId: selectedSchemeId },
    skip: !selectedSchemeId,
    fetchPolicy: 'network-only',
  });

  const [appendBind, { loading }] = useMutation(APPEND_BIND_PRICING_SCHEME);

  useEffect(() => {
    const applyDetailResult = applyDetail?.getClientPricingSchemeApplyDetail;
    if (applyDetailResult?.messageKey || applyDetailResult?.message) {
      message.error(resolvePricingSchemeMessage(applyDetailResult, '加载客户方案绑定详情失败'));
    }
    const detailResult = detailData?.getPricingSchemeDetail;
    if (detailResult?.messageKey || detailResult?.message) {
      message.error(resolvePricingSchemeMessage(detailResult, '加载定价方案详情失败'));
    }
  }, [applyDetail, detailData]);

  const getTiers = () => {
    const config = detailData?.getPricingSchemeDetail?.priceConfig?.find(
      p => Number(p.pricingFeeItemCode) === COST_PERIODIC_FEE_ITEM_CODE
    );
    if (!config?.allPriceConfig?.[0]?.contentPriceConfig) return [];
    try { return JSON.parse(config.allPriceConfig[0].contentPriceConfig); } catch { return []; }
  };

  const boundValues: string[] = applyDetail?.getClientPricingSchemeApplyDetail?.bindValues ?? [];
  const tiers = getTiers();

  // exclude already bound hubs from options
  const availableHubs = (hubOptions?.allOptions ?? []).filter(
    (o: { value: string }) => !boundValues.includes(o.value)
  );
  // exclude already bound warehouses (二段码) from options
  const availableWarehouses = (warehouseOptions?.allOptions ?? []).filter(
    (o: { value: string }) => !boundValues.includes(o.value)
  );

  // 本次追加的 bindValue：到 HUB 用一段码列表，到 Warehouse 用二段码列表
  const newBindValues = granularity === 'HUB' ? newHubs : newWarehouses;

  // 若该方案已绑定过兜底值 ALL，隐藏新增绑定维度整块区域，不允许再新增
  const hasAllBound = boundValues.includes(COST_PERIODIC_BIND_ALL_VALUE);

  const doEdit = async () => {
    try {
      const { data, errors } = await appendBind({
        variables: {
          input: { clientId: 0, pricingSchemeId: selectedSchemeId!, bindValue: newBindValues },
        },
      });
      if (errors?.length) { message.error(errors[0].message); return; }
      if (data?.appendBindPricingSchema?.success) {
        message.success(t('settlement.priceCenter.messages.bindEditSuccess'));
        onSuccess?.();
      } else {
        message.error(resolvePricingSchemeMessage(data?.appendBindPricingSchema, t('settlement.priceCenter.messages.operationFailed')));
      }
    } catch (e: any) {
      message.error(e?.message || t('errors.INTERNAL_ERROR'));
      throw e;
    }
  };

  const handleEdit = () => {
    if (!selectedSchemeId) { message.error('请选择方案'); return; }
    if (granularity === 'HUB' && newHubs.length === 0) { message.error('请选择要新增绑定的 HUB'); return; }
    if (granularity === 'WAREHOUSE') {
      if (!warehouseHub) { message.error('请选择 HUB'); return; }
      if (newWarehouses.length === 0) { message.error('请选择要新增绑定的 Warehouse'); return; }
    }

    Modal.confirm({
      title: '确认追加绑定方案',
      icon: <span style={{ color: '#ff4d4f', fontSize: '22px' }}>⚠️</span>,
      content: (
        <div>
          <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#ff4d4f' }}>
            警告：此操作不可撤销！
          </p>
          <p style={{ marginBottom: '8px' }}>您即将为以下信息追加绑定：</p>
          <div style={{ padding: '12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px', marginTop: '12px' }}>
            <p><strong>方案ID：</strong>{selectedSchemeId}</p>
            <p><strong>追加绑定维度：</strong>{newBindValues.join('、')}</p>
          </div>
          <p style={{ marginTop: '16px', color: '#ff4d4f' }}>
            追加绑定后，该方案将对这些新增维度生效。
          </p>
          <p style={{ marginTop: '8px', color: '#666' }}>
            生效时间/时区将沿用原绑定方案（不可编辑）。
          </p>
        </div>
      ),
      okText: '确认追加绑定',
      okType: 'danger',
      cancelText: '取消',
      width: 520,
      onOk: doEdit,
    });
  };

  return (
    <div>
      {/* 方案选择 + 备注 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <span className="font-medium">选择方案</span>
        <Select
          style={{ width: 200 }}
          placeholder="请选择"
          options={schemeOptions?.allOptions?.map((o: { value: string; label: string }) => ({
            value: Number(o.value),
            label: o.label,
          }))}
          value={selectedSchemeId}
          onChange={v => { setSelectedSchemeId(v); setNewHubs([]); setWarehouseHub(null); setNewWarehouses([]); }}
        />
        <span className="font-medium ml-4">方案备注</span>
        <span style={{ color: '#555' }}>{detailData?.getPricingSchemeDetail?.pricingSchemeDescription ?? ''}</span>
      </div>

      {/* 方案详情 Tabs 展示 */}
      {tiers.length > 0 && (
        <Tabs size="middle" items={[{
          key: 'nthOrderSameAddrDiscount',
          label: '第N件N折',
          children: (
            <div>
              <div style={{ display: 'flex', fontWeight: 500, fontSize: 13, color: '#555', paddingBottom: 8, borderBottom: '1px solid #f0f0f0', marginBottom: 8 }}>
                <span style={{ width: 160, flexShrink: 0 }}>费用类型</span>
                <span style={{ flex: 1 }}>阶梯</span>
                <span style={{ width: 170, flexShrink: 0, textAlign: 'right' }}>折扣类型</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <span style={{ width: 160, flexShrink: 0, paddingTop: 4 }}>DSP基础运费</span>
                <div style={{ flex: 1 }}>
                  {tiers.map((tier: any, i: number) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ flex: 1, color: '#333' }}>{tier.startOrderCount} - {tier.endOrderCount} 单</span>
                      <span style={{ width: 170, textAlign: 'right', color: '#888' }}>{formatTierDiscountDisplay(tier)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ),
        }]} style={{ marginBottom: 0 }} />
      )}

      {selectedSchemeId && (
        <>
          <Divider />
          {boundValues.length > 0 && (
            <div className="mb-4" style={{ color: '#555' }}>
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontWeight: 500 }}>当前已绑定维度：</span>
                <span>{boundValues.join('、')}</span>
              </div>
              <div>
                <span style={{ fontWeight: 500 }}>生效时间：</span>
                <span>
                  {formatApplyTimeWithTimezone(
                    applyDetail?.getClientPricingSchemeApplyDetail?.effectiveTime,
                    applyDetail?.getClientPricingSchemeApplyDetail?.timezone
                  )}
                </span>
              </div>
            </div>
          )}
          {!hasAllBound && (
            <div className="mb-4">
              <div className="font-medium mb-2">新增绑定维度：</div>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Radio.Group
                  value={granularity}
                  onChange={e => {
                    setGranularity(e.target.value);
                    setNewHubs([]);
                    setWarehouseHub(null);
                    setNewWarehouses([]);
                  }}
                >
                  <Radio value="HUB">到 HUB</Radio>
                  <Radio value="WAREHOUSE">到 Warehouse</Radio>
                </Radio.Group>
                {granularity === 'HUB' ? (
                  <Select
                    mode="multiple"
                    style={{ width: 400 }}
                    placeholder="请选择要新增绑定的 HUB"
                    options={availableHubs}
                    value={newHubs}
                    onChange={setNewHubs}
                  />
                ) : (
                  <Space align="start" size={8}>
                    <Select
                      showSearch
                      style={{ width: 200 }}
                      placeholder="请选择 HUB"
                      optionFilterProp="label"
                      options={hubOptions?.allOptions}
                      value={warehouseHub}
                      onChange={v => { setWarehouseHub(v); setNewWarehouses([]); }}
                    />
                    <Select
                      mode="multiple"
                      showSearch
                      style={{ width: 400 }}
                      placeholder="按二段码搜索 Warehouse"
                      optionFilterProp="label"
                      disabled={!warehouseHub}
                      options={availableWarehouses}
                      value={newWarehouses}
                      onChange={setNewWarehouses}
                    />
                  </Space>
                )}
              </Space>
            </div>
          )}
        </>
      )}

      <div className="flex justify-end gap-4 mt-6">
        <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_APPLY_OPERATE]}>
          <Button type="primary" onClick={handleEdit} loading={loading} disabled={!selectedSchemeId || hasAllBound || newBindValues.length === 0}>
            确认
          </Button>
        </HasResource>
        <Button onClick={onCancel}>返回</Button>
      </div>
    </div>
  );
};

export default CostPeriodicBindEditComp;
